import type { ColumnOrderState, ColumnPinningState, ColumnSizingState, VisibilityState } from '@tanstack/react-table';
import type { GridSpacing } from '../components/Grid';

const STORAGE_PREFIX = 'table-settings-';

export const DEFAULT_PAGE_SIZE = 50;

export type TableSettings = {
  spacing?: GridSpacing;
  pageSize?: number;
  columnOrder?: ColumnOrderState;
  columnPinning?: ColumnPinningState;
  columnVisibility?: VisibilityState;
  columnSizing?: ColumnSizingState;
};

/**
 * Get table settings from localStorage for a specific table ID.
 * Returns null if no settings exist or if there's an error.
 */
export function getTableSettings(id: string): TableSettings | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const key = `${STORAGE_PREFIX}${id}`;
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as TableSettings;
    return parsed;
  } catch (error) {
    console.error(`Failed to read table settings for id "${id}":`, error);
    return null;
  }
}

/**
 * Save table settings to localStorage for a specific table ID.
 * Silently fails if localStorage is unavailable or if there's an error.
 */
export function saveTableSettings(id: string, settings: TableSettings): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const key = `${STORAGE_PREFIX}${id}`;
    localStorage.setItem(key, JSON.stringify(settings));
  } catch (error) {
    // Handle quota exceeded or other storage errors
    console.error(`Failed to save table settings for id "${id}":`, error);
  }
}

/**
 * Clear table settings from localStorage for a specific table ID.
 */
export function clearTableSettings(id: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const key = `${STORAGE_PREFIX}${id}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to clear table settings for id "${id}":`, error);
  }
}
